import { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";

import { useAppStatus } from "../../hooks/useAppStatus";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";
import { invalidateSystemAppsCache } from "../../hooks/useSystemApps";
import { cn } from "../../utils/cn";
import { AppUpdateModal } from "../apps/AppUpdateModal";
import { useAuth } from "../auth/AuthProvider";
import { Icon } from "../ui/Icon";
import { Popover } from "../ui/Popover";
import { Tooltip } from "../ui/Tooltip";

type AppStatusDotProps = {
  appId: string;
  appLabel: string;
  appPath: string;
};

type ToneKey = "healthy" | "update" | "degraded" | "unknown";

const DOT_SIZE = "h-2.5 w-2.5";

const toneClasses: Record<ToneKey, { dot: string; badge?: string }> = {
  healthy: {
    dot: "bg-emerald-500 shadow-[0_0_0_6px_rgba(16,185,129,0.18)]",
    badge: "text-emerald-600 dark:text-emerald-400",
  },
  update: {
    dot: "bg-amber-500 shadow-[0_0_0_6px_rgba(245,158,11,0.2)]",
    badge: "text-amber-600 dark:text-amber-400",
  },
  degraded: {
    dot: "bg-rose-500 shadow-[0_0_0_6px_rgba(244,63,94,0.2)]",
    badge: "text-rose-600 dark:text-rose-400",
  },
  unknown: {
    dot: "bg-muted-foreground/40 shadow-[0_0_0_5px_rgba(156,163,175,0.15)]",
    badge: "text-muted-foreground",
  },
};

const StatusLine = ({
  tone,
  statusLabel,
  message,
}: {
  tone: ToneKey;
  statusLabel: string;
  message?: string;
}) => (
  <div className={cn("flex items-start gap-2 text-sm", toneClasses[tone]?.badge)}>
    <Icon icon="lucide:activity" className="mt-[1px] h-4 w-4" />
    <div className="leading-tight">
      <div className="font-medium text-foreground">{statusLabel}</div>
      {message && <div className="text-[12px] text-muted-foreground">{message}</div>}
    </div>
  </div>
);

const VersionRow = ({ label, value }: { label: string; value: string | null }) => (
  <div className="flex items-center justify-between gap-2 text-sm text-foreground">
    <span className="text-muted-foreground">{label}</span>
    <code className="rounded bg-muted/60 px-1.5 py-[2px] font-mono text-[11px] leading-tight text-foreground/90">
      {value ?? "—"}
    </code>
  </div>
);

export const AppStatusDot = ({ appId, appLabel }: AppStatusDotProps) => {
  const { data, loading } = useAppStatus(appId);
  const { user } = useAuth();
  const prefersReducedMotion = usePrefersReducedMotion();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  const isAdmin = (user?.userLevel ?? 0) >= 9;

  const visualTone: ToneKey = useMemo(() => {
    if (data.status === "degraded" || data.status === "stopped") return "degraded";
    if (data.updateAvailable) return "update";
    if (data.status === "healthy") return "healthy";
    return "unknown";
  }, [data.status, data.updateAvailable]);

  const isPackagePending = data.updateAvailable && !data.packageAvailable;

  const statusLabel = useMemo(() => {
    if (data.status === "stopped") return "Stopped";
    if (data.status === "degraded") return "Degraded";
    if (data.updateAvailable && !data.packageAvailable) return "Update pending";
    if (data.updateAvailable) return "Update available";
    if (data.status === "healthy") return "Running";
    return "Unknown";
  }, [data.status, data.updateAvailable, data.packageAvailable]);

  const tooltipLabel = `${appLabel} status — click for details`;

  const popoverContent = (close: () => void) => (
    <div className="min-w-[240px] max-w-[300px] space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-foreground">{appLabel}</div>
          <div className="text-xs text-muted-foreground">App status</div>
        </div>
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="rounded-full p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
        >
          <Icon icon="lucide:x" className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-2">
        <StatusLine tone={visualTone} statusLabel={statusLabel} message={data.message} />
        <VersionRow label="Installed" value={data.installedVersion} />
        <VersionRow label="Latest" value={data.latestVersion} />
        {data.updatedAt && (
          <div className="text-xs text-muted-foreground">Updated {data.updatedAt}</div>
        )}
      </div>

      {data.updateAvailable && isPackagePending && (
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          A new version has been detected but the download package has not been published
          upstream yet. The update will become available once the package is released.
        </p>
      )}

      {data.updateAvailable && !isPackagePending && isAdmin && (
        <button
          type="button"
          onClick={() => {
            close();
            setShowUpdateModal(true);
          }}
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm font-medium text-amber-600 transition-colors hover:bg-amber-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/40 dark:text-amber-400"
        >
          <Icon icon="lucide:arrow-up-circle" className="h-3.5 w-3.5" />
          Update to {data.latestVersion ?? "latest"}
        </button>
      )}

      {data.managePath && (
        <NavLink
          to={data.managePath}
          className="flex items-center justify-between rounded-md border border-border/60 bg-muted/70 px-3 py-2 text-sm text-foreground transition-colors hover:bg-muted"
          onClick={close}
        >
          <span>Manage</span>
          <Icon icon="lucide:chevron-right" className="h-4 w-4 text-muted-foreground" />
        </NavLink>
      )}
    </div>
  );

  // Detect stopped/degraded + update available for dual-indicator styling:
  // red dot (stopped) with amber ring (update available)
  const stoppedWithUpdate =
    (data.status === "stopped" || data.status === "degraded") && data.updateAvailable;

  const tone = toneClasses[visualTone];
  const pulseClass = prefersReducedMotion ? "" : "animate-pulse";

  return (
    <>
    <div className="shrink-0" onClick={(event) => { event.preventDefault(); event.stopPropagation(); }}>
      <Tooltip
        content={tooltipLabel}
        side="top"
        align="center"
        disabled={popoverOpen}
        panelClassName="min-w-[200px] max-w-[240px] text-left"
      >
        <Popover
          side="top"
          align="center"
          stopPropagation
          onOpenChange={setPopoverOpen}
          trigger={
            <span
              className={cn(
                DOT_SIZE,
                "rounded-full",
                tone.dot,
                pulseClass,
                loading ? "opacity-80" : "",
                stoppedWithUpdate && "ring-2 ring-amber-400/70 ring-offset-1 ring-offset-card"
              )}
              aria-label={`${appLabel} status`}
            />
          }
          renderContent={popoverContent}
          panelClassName="rounded-xl border border-border/60 bg-card/95 p-4 shadow-2xl backdrop-blur"
        />
      </Tooltip>
    </div>
    {isAdmin && data.packageAvailable && (
      <AppUpdateModal
        open={showUpdateModal}
        onClose={() => setShowUpdateModal(false)}
        onComplete={() => invalidateSystemAppsCache()}
        appLabel={appLabel}
        packageName={appId}
        installedVersion={data.installedVersion}
        latestVersion={data.latestVersion}
      />
    )}
    </>
  );
};
